import { Log, LogCategory, Operation as LogOperation } from '@augloop/logging';
import { ExceptionType } from '@augloop/schemas';
export class BatchManager {
    constructor(submit, onSummary) {
        this.batchesByGroupingKey = new Map();
        this.submit = submit;
        this.onSummary = onSummary;
    }
    addBatchItem(input, batchConfig, context, cv, groupComplete, callback) {
        const { delayMs, maxInputSize, estimateSize, groupingKeyExtractor } = batchConfig;
        const inputSize = estimateSize(input);
        const groupingKey = groupingKeyExtractor(input);
        // If input is larger than maxInputSize, split it into multiple inputs
        if (batchConfig.split && inputSize > maxInputSize) {
            let splitResult;
            try {
                splitResult = batchConfig.split(input);
            }
            catch (err) {
                const splitError = new Error('Split error: ' + err.message);
                if (callback) {
                    callback(splitError);
                }
                else {
                    Log.error(0x222ce050 /* tag_8lobq */, LogCategory.CoreDefault, splitError);
                }
                return;
            }
            if (splitResult && Array.isArray(splitResult.inputs) && splitResult.inputs.length > 1) {
                // Make sure we invoke the callback once even though we have multiple inputs
                let error;
                const outputs = [];
                const splitCallback = callback
                    ? (err, output) => {
                        error = error || err; // Take the first error
                        outputs.push(output);
                        if (outputs.length === splitResult.inputs.length) {
                            let output;
                            try {
                                output = splitResult.join(outputs);
                            }
                            catch (err) {
                                callback(new Error('Join error: ' + err.message));
                                return;
                            }
                            callback(error, output);
                        }
                    }
                    : undefined;
                // Add each input to the batch individually and keep groupComplete flag just for the last one
                for (let index = 0; index < splitResult.inputs.length; index++) {
                    const splitInput = splitResult.inputs[index];
                    this.addBatchItem(splitInput, batchConfig, context, cv, groupComplete && index === splitResult.inputs.length - 1, splitCallback);
                }
                return;
            }
        }
        // Get the existing batch and send it if adding the given input to it would increase the size beyond the max
        let batch = this.batchesByGroupingKey.get(groupingKey);
        if (batch && batch.size + inputSize > maxInputSize) {
            clearTimeout(batch.timeout);
            this.executeBatch(groupingKey, batch, batchConfig);
            batch = undefined;
        }
        // Form a new batch if necessary
        if (!batch) {
            batch = {
                items: [],
                size: 0,
                hasItemsWithCallbacks: false,
                context: context
            };
            this.batchesByGroupingKey.set(groupingKey, batch);
        }
        // Add the given input to the current batch and schedule it for submission
        batch.items.push({
            input: input,
            size: inputSize,
            callback: callback
        });
        batch.size += inputSize;
        batch.hasItemsWithCallbacks = batch.hasItemsWithCallbacks || !!callback;
        batch.cv = cv; // Service request will use the last CV seen
        batch.groupComplete = groupComplete;
        clearTimeout(batch.timeout); // No-op if undefined/invalid
        if (batch.groupComplete) {
            this.executeBatch(groupingKey, batch, batchConfig);
        }
        else {
            batch.timeout = setTimeout(() => {
                this.executeBatch(groupingKey, batch, batchConfig);
            }, delayMs);
        }
    }
    removeAllBatchedItems() {
        this.batchesByGroupingKey.forEach(batch => {
            if (batch.timeout) {
                clearTimeout(batch.timeout);
            }
        });
        this.batchesByGroupingKey.clear();
    }
    executeBatch(groupingKey, batch, batchConfig) {
        this.batchesByGroupingKey.delete(groupingKey);
        const batchItems = batch.items;
        const getOrderOfMagnitudeDimension = (size) => {
            if (size < 0) {
                return 'Negative';
            }
            if (size === 0) {
                return '0';
            }
            if (size === 1) {
                return '1';
            }
            return `Magnitude ${size.toString().length}`;
        };
        const op = new LogOperation({
            operationName: 'ExecuteBatch',
            cv: batch.cv,
            resourceId: batch.context.name,
            dimension0: `Count ${getOrderOfMagnitudeDimension(batchItems.length)}`,
            dimension1: `Size ${getOrderOfMagnitudeDimension(batch.size)}`
        }).start();
        const invokeOutputCallbacksWithError = (operation, err, output) => {
            let error;
            if (err && err.exceptionType === ExceptionType.NoOutput) {
                op.success = true;
                Log.info(0x222ce051 /* tag_8lobr */, LogCategory.CoreDefault, op.stop());
            }
            else {
                const errorMessage = `${operation} error: ${err ? (err.message || err) : 'Unknown error'}`;
                error = err || new Error(errorMessage);
                op.success = false;
                op.resultDescription = `Batch of ${batchItems.length} items of size ${batch.size} with ${errorMessage}`;
                op.resultSignature = `${operation} Error`;
                Log.error(0x222ce052 /* tag_8lobs */, LogCategory.CoreDefault, op.stop());
            }
            if (batch.hasItemsWithCallbacks) {
                for (const batchItem of batchItems) {
                    if (batchItem.callback) {
                        batchItem.callback(error, output);
                    }
                }
            }
        };
        // Combine batch into multiplexResult and submit it
        let multiplexResult;
        try {
            multiplexResult = batchConfig.multiplex(batchItems.map(x => x.input));
        }
        catch (err) {
            invokeOutputCallbacksWithError('Multiplex', err);
            return;
        }
        this.submit(multiplexResult.input, batch, (err, output) => {
            if (err || (output && ('exceptionType' in output || output instanceof Error))) {
                invokeOutputCallbacksWithError('Submit', err || output, output);
            }
            else if (batch.hasItemsWithCallbacks || batchConfig.summaryExtractor) {
                let outputs;
                if (batch.hasItemsWithCallbacks) {
                    try {
                        outputs = multiplexResult.demultiplex(output);
                        if (outputs.length !== batchItems.length) {
                            throw new Error('Mismatched output length');
                        }
                    }
                    catch (err) {
                        invokeOutputCallbacksWithError('Demultiplex', err);
                        return;
                    }
                }
                let summary;
                let summarySchemaName;
                if (batchConfig.summaryExtractor) {
                    try {
                        [summarySchemaName, summary] = batchConfig.summaryExtractor(output);
                    }
                    catch (err) {
                        invokeOutputCallbacksWithError('SummaryExtractor', err);
                        return;
                    }
                }
                op.success = true;
                Log.info(0x222ce046 /* tag_8lobg */, LogCategory.CoreDefault, op.stop());
                if (batch.hasItemsWithCallbacks) {
                    for (let i = 0; i < batchItems.length; i++) {
                        if (batchItems[i].callback) {
                            batchItems[i].callback(undefined, outputs[i]);
                        }
                    }
                }
                if (summary) {
                    this.onSummary(summarySchemaName, summary, batch.context);
                }
            }
            else {
                op.success = true;
                Log.info(0x222ce047 /* tag_8lobh */, LogCategory.CoreDefault, op.stop());
            }
        });
    }
}
//# sourceMappingURL=batch-manager.js.map